import { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import { notAllowed, NAME_LANG_BY_TYPE } from 'validations';
import { getNewestTicket, handleThaiUniqueString, pickCompressedImage } from 'helpers';

// Data
import disabilities from 'data/disabilities';

const useBasicInfo = (profile = {}, pendingTickets = [], citizenIdType) => {
  // ล็อกภาษาช่องชื่อหลักตามชนิดบัตร; ช่องชื่อเพิ่มเติมตรวจภาษาตอนบันทึกเพื่อให้แก้ค่าเดิมที่ผิดรูปแบบได้
  const _caseOf = { th: 'nameTH', en: 'nameEN' };
  const nameLangCase = _caseOf[NAME_LANG_BY_TYPE[citizenIdType]];
  // ค่าเดิมผิดภาษา (เช่น ช่องไทยเป็น "Kulpriya") ต้องลบทีละตัวได้ — บล็อกเฉพาะตอนพิมพ์เพิ่ม, ภาษาตรวจอีกรอบตอนบันทึก
  const nameLangBlocked = (value, prev) => nameLangCase && notAllowed(nameLangCase, value) && value.length >= prev.length;
  const { valueOf: nameValueOf } = getNewestTicket(pendingTickets, 'update_name');
  const { valueOf: srValueOf } = getNewestTicket(pendingTickets, 'update_special_request');

  const [email, setEmail] = useState(profile.email || '');
  const [mobile, setMobile] = useState(profile.telephone || '');
  const [title, setTitle] = useState(nameValueOf('title') || profile.title || '');
  const [firstName, setFirstName] = useState(nameValueOf('first_name') || profile.first_name || '');
  const [lastName, setLastName] = useState(nameValueOf('last_name') || profile.last_name || '');
  const [firstNameAdditional, setFirstNameAdditional] = useState(profile.first_name_additional || '');
  const [lastNameAdditional, setLastNameAdditional] = useState(profile.last_name_additional || '');
  const [weight, setWeight] = useState(profile.weight || '');
  const [height, setHeight] = useState(profile.height || '');
  const [birthDate, setBirthDate] = useState(nameValueOf('birth_date') || profile.birth_date || '');
  const [studentCode, setStudentCode] = useState(profile.student_code || '');

  const disabilityIndex = _.findIndex(disabilities, text => text === (srValueOf('special_request') || profile.special_request));
  const [specialRequestVisibility, setSpecialRequestVisibility] = useState(disabilityIndex !== -1);
  const [specialRequest, setSpecialRequest] = useState(disabilityIndex === -1 ? null : disabilities[disabilityIndex]);
  const [specialRequestFile, setSpecialRequestFile] = useState();

  useEffect(() => {
    if (!specialRequestVisibility) { setSpecialRequest(); }
  }, [specialRequestVisibility]);

  return {
    email,
    mobile,
    title,
    firstName,
    lastName,
    firstNameAdditional,
    lastNameAdditional,
    weight,
    height,
    birthDate,
    studentCode,
    specialRequestVisibility,
    specialRequest,
    specialRequestFile,

    fullNameIsPending:
      (nameValueOf('first_name') && nameValueOf('first_name') !== profile.first_name) ||
      (nameValueOf('last_name') && nameValueOf('last_name') !== profile.last_name),

    onEmailChange: (e) => {
      if (notAllowed('email', e.target.value)) return;
      // lowercase ตั้งแต่กรอก — BE cleanseEmail lowercase อยู่แล้ว ให้เด็กเห็นค่าเดียวกับที่ถูกเก็บ
      setEmail(e.target.value.trim().toLowerCase());
    },
    onMobileChange: (e) => {
      if (notAllowed('mobile', e.target.value)) return;
      setMobile(e.target.value);
    },
    onTitleChange: (value) => {
      setTitle(value);
    },
    onFirstNameChange: (e) => {
      const value = handleThaiUniqueString(e.target.value);
      if (notAllowed('textTH', value)) return;
      if (nameLangBlocked(value, firstName)) return;
      setFirstName(value);
    },
    onLastNameChange: (e) => {
      if (e.target.value === '-') {
        setLastName('ไม่มีนามสกุล');
      } else {
        const value = handleThaiUniqueString(e.target.value);
        if (notAllowed('textTH', value)) return;
        if (nameLangBlocked(value, lastName)) return;
        setLastName(value);
      }
    },
    onFirstNameAdditionalChange: (e) => {
      const value = handleThaiUniqueString(e.target.value);
      setFirstNameAdditional(value);
    },
    onLastNameAdditionalChange: (e) => {
      const value = handleThaiUniqueString(e.target.value);
      setLastNameAdditional(value);
    },
    onWeightChange: (e) => {
      if (notAllowed('weight', e.target.value)) return;
      setWeight(e.target.value);
    },
    onHeightChange: (e) => {
      if (notAllowed('height', e.target.value)) return;
      setHeight(e.target.value);
    },
    onBirthDateChange: (e) => {
      setBirthDate(e.target.value);
    },
    onStudentCodeChange: (e) => {
      const num = e.target.value;
      if (num && isNaN(num)) return;
      setStudentCode(e.target.value);
    },
    onSpecialRequestChange: (e) => {
      setSpecialRequest(disabilities[e.target.value]);
    },
    onSpecialRequestVisibilityChange: (e) => {
      setSpecialRequestVisibility(e.target.checked)
    },
    onSpecialRequestFileChange: async (e) => {
      const _file = await pickCompressedImage(e);
      if (_file) setSpecialRequestFile(_file);
    },

    prefill: useCallback((profile, ticket) => {
      setEmail(profile.email || '');
      setMobile(profile.telephone || '');
      setTitle(_.get(ticket, 'requested_data.title.value') || profile.title || '');
      setFirstName(_.get(ticket, 'requested_data.first_name.value') || profile.first_name || '');
      setLastName(_.get(ticket, 'requested_data.last_name.value') || profile.last_name || '');
      setFirstNameAdditional(profile.first_name_additional || '');
      setLastNameAdditional(profile.last_name_additional || '');
      setBirthDate(_.get(ticket, 'requested_data.birth_date.value') || profile.birth_date || '');
      setWeight(profile.weight || '');
      setHeight(profile.height || '');
    }, []),
  };
};

export default useBasicInfo;
